import { message } from "antd";
import { convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { setTemplateData, setServerData } from "../app/EditorSlice";
import { padServices } from "../api";

export const generateHtml = (contentState) => {
  let rawContentState = convertToRaw(contentState);
  rawContentState.blocks = rawContentState.blocks.map(block => {
    if (block.type.startsWith('header')) {
      if (!block.data.id) {
        block.data.id = `heading-${block.key}`;
      }
    }
    const newData = {
      ...block.data,
      ...(block.data.lineHeight ? { lineHeight: block.data.lineHeight } : {}),
      ...(block.data.textAlignment ? { 'text-align': block.data.textAlignment } : {}),
    };
    return {
      ...block,
      data: newData
    };
  });
  // Convert to HTML
  const htmlWithIDs = draftToHtml(rawContentState);
  return htmlWithIDs;
};

export const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0";

  const units = ["Bytes", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  if (unitIndex === 0) {
    return `${size} ${units[unitIndex]}`;
  } else if (unitIndex === 1 && size < 50) {
    return `${parseFloat(size.toFixed(0))} ${units[unitIndex]}`;
  } else {
    return `${parseFloat(size.toFixed(2))} ${units[unitIndex]}`;
  }
};

export const adjustEditorHeightWithSignatures = (signatures) => {
  let pageContainer = document.getElementById('lexical-editor-container');
  if (!signatures || signatures.length === 0 || !pageContainer) {
    return;
  }
  const greatestTopSignature = signatures.reduce((max, signature) => {
    const currentTop = parseInt(signature?.signatureBoxStyle?.top, 10) || -Infinity;
    return currentTop > max.top ? { signature, top: currentTop } : max;
  }, { signature: null, top: -Infinity }).signature;
  if (greatestTopSignature) {
    const top = parseInt(greatestTopSignature.signatureBoxStyle.top, 10) || 0;
    const pageHeight = pageContainer.clientHeight;
    if (pageHeight < top) {
      pageContainer.style.minHeight = `${top + 100}px`;
    }
  }
};

export const showPagebreak = () => {
  const pageContainer = document.getElementById('lexical-editor-container');
  const editorContainer = document.getElementById('lexical-pages-container');
  if (!pageContainer || !editorContainer) return;

  // Remove existing page break lines
  document.querySelectorAll('.page-break').forEach(breakElement => breakElement.remove());

  // Legacy visual-only pagination (dotted overlay lines) has been replaced by
  // Lexical page breaks + PaginationPlugin.
  return;
};
export const isRespectiveDocument = (documentId) => {
  const pathname = window.location.pathname;
  let id = pathname.split("/")[1];
  if (id === "external") {
    id = pathname.split("/")[2]
  }
  return id && id === documentId;
};


export const getLocationCoordinates = (success, error) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(success, error);
  } else {
    message.error("Geolocation is not supported by this browser.");
  }

  // function success(position) {
  //   x.innerHTML = "Latitude: " + position.coords.latitude + 
  //   "<br>Longitude: " + position.coords.longitude;
  // }
};

export const fetchIpAddress = () => {
  return new Promise((resolve, reject) => {
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then((data) => resolve(data.ip))
      .catch(error => reject(error));
  });
};

export const saveToLocalStorage = (docId, data) => {
  try {
    const localData = {
      data: data,
      lastUpdateTime: new Date().toISOString()
    };
    // Retrieve existing data from localStorage or initialize with an empty object
    const existingData = JSON.parse(localStorage.getItem('offlineMilepadData')) || {};
    // Replace or add the sheet data for the given docId
    existingData[docId] = localData;
    // Save updated data back to localStorage
    localStorage.setItem('offlineMilepadData', JSON.stringify(existingData));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const removeOfflineData = (id) => {
  const offlineData = JSON.parse(localStorage.getItem('offlineMilepadData') || '{}');
  delete offlineData[id];
  localStorage.setItem('offlineMilepadData', JSON.stringify(offlineData));
};

export const safeParseEditorState = (editor, jsonStringOrObject) => {
  const defaultEmptyState = '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
  if (!jsonStringOrObject) {
    return editor.parseEditorState(defaultEmptyState);
  }
  try {
    // Accept either a string or already-parsed object.
    // Also handle "double-encoded" JSON that may come back as a JSON string literal.
    let jsonString = '';

    if (typeof jsonStringOrObject === 'string') {
      const trimmed = jsonStringOrObject.trim();
      if (trimmed === '') {
        return editor.parseEditorState(defaultEmptyState);
      }

      // If the backend stored the editorState as a JSON string literal ("{...}")
      // JSON.parse will unwrap it back to "{...}".
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
          jsonString = parsed;
        } else if (parsed && typeof parsed === 'object') {
          jsonString = JSON.stringify(parsed);
        } else {
          jsonString = trimmed;
        }
      } catch {
        jsonString = trimmed;
      }
    } else {
      jsonString = JSON.stringify(jsonStringOrObject);
    }

    if (!jsonString || jsonString.trim() === '') {
      return editor.parseEditorState(defaultEmptyState);
    }

    return editor.parseEditorState(jsonString);
  } catch (error) {
    console.error('Failed to parse editor state:', error);
    return editor.parseEditorState(defaultEmptyState);
  }
};

export const loadTemplateIfAvailable = async ({
  searchParams,
  setSearchParams,
  id,
  dispatch,
  setContentLoading
}) => {
  const templateId = searchParams.get('templateId');

  try {
    // Fetch template data
    const serverResponse = await padServices.fetchTemplateById(templateId);
    const templateData = serverResponse?.data?.data;

    // Store template data in Redux
    dispatch(setTemplateData(templateData));

    // Prepare the data for saving
    const dataToLoad = {
      ...serverResponse?.data,
      summary: "",
      Signatures: [],
      milepadData: templateData?.milepadData || "",
    };

    // Save under current doc id
    await padServices.saveToDatabase(
      id,
      dataToLoad.milepadData,
      dataToLoad.summary,
      dispatch
    );

    // Update redux
    dispatch(setServerData(dataToLoad));

    // Remove templateId from URL
    setSearchParams((params) => {
      params.delete('templateId');
      return params;
    });

    setContentLoading(false);

    // Return loaded data
    return dataToLoad;

  } catch (err) {
    console.error("Error loading template:", err);
    return null;
  }
};